import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { Table, TextCell } from "@similarweb/pro-logic-components";

import { abbrNumberFilter, percentageFilter } from "app/filters/ngFilters";

import { useMetricWidgetContext } from "../../common/widget/donut-metric/MetricWidgetContext";
import type { IChartDataItem } from "../../common/widget/donut-metric/types";
import { rowIndexColumn } from "../../common/widget/table/rowIndexColumn";

import { DomainCell } from "./DomainCell";

const abbrNumber = abbrNumberFilter();

interface IContentActivityTableRow {
    domain: string;
    icon?: string | null;
    share: number;
    value: number;
    isPrimary: boolean;
    isMaxValue: boolean;
}

const toTableData = (chartData: IChartDataItem[]): IContentActivityTableRow[] =>
    chartData.map((item) => ({
        domain: item.name,
        icon: item.icon,
        share: item.y,
        value: item.value,
        isPrimary: !!item.isPrimary,
        isMaxValue: !!item.isMaxValue,
    }));

const useColumnsConfig = () => {
    const { translate } = useTranslate();
    return useMemo(
        () =>
            [
                rowIndexColumn,
                {
                    title: translate("activation.alerts.metrics.legend.domain"),
                    dataIndex: "domain",
                    render: (value: string, record: IContentActivityTableRow) => (
                        <DomainCell
                            domain={value}
                            favicon={record.icon}
                            fontVariant={record.isPrimary ? "subtitle2" : "body2"}
                            showTrophy={record.isMaxValue}
                        >
                            {record.isPrimary
                                ? translate("activation.common.your-domain", {
                                      domain: value,
                                  })
                                : value}
                        </DomainCell>
                    ),
                    minWidth: 200,
                },
                {
                    title: translate("activation.alerts.metrics.legend.share"),
                    dataIndex: "share",
                    align: "right",
                    render: (value: number) => <TextCell>{`${percentageFilter()(value, 0)}%`}</TextCell>,
                    width: 120,
                },
                {
                    title: translate("activation.alerts.metrics.legend.total-posts"),
                    dataIndex: "value",
                    align: "right",
                    render: (value: number) => <TextCell>{abbrNumber(value)}</TextCell>,
                    width: 120,
                },
            ] satisfies ColumnsType<IContentActivityTableRow>,
        [translate],
    );
};

export const ContentActivityTable = () => {
    const {
        state: { fullData },
    } = useMetricWidgetContext();
    const tableData = useMemo(() => (fullData ? toTableData(fullData) : []), [fullData]);
    const columnsConfig = useColumnsConfig();

    return (
        <Table<IContentActivityTableRow>
            data={tableData}
            columnConfig={columnsConfig}
            tableLayout="auto"
            highlightedRowSelector={(record) => record.isPrimary}
        />
    );
};
